// The person's own menu at the right of the top (BL-135): Your sign-in, and how the app looks to them,
// Light, Dark or Match my device (the default). The pick draws at once and is remembered on this
// device (theme.ts); public/theme-boot.js draws it before the page does next time.
import { useEffect, useId, useRef, useState } from 'react';
import { applyTheme, readTheme, saveTheme, THEME_CHOICES, type ThemeChoice } from './theme.ts';

export function MeMenu({ current, onSignIn }: { current: boolean; onSignIn: () => void }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(() => readTheme());
  const [kept, setKept] = useState(true);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  // a press anywhere else, or Esc, closes it (Esc gives the button its focus back)
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const pick = (t: ThemeChoice) => {
    setTheme(t);
    applyTheme(t);
    setKept(saveTheme(t));
  };

  return (
    <div className="bp-me-menu" ref={root}>
      <button
        ref={button}
        type="button"
        className="bp-me"
        aria-label="You: your sign-in, light or dark"
        aria-expanded={open}
        aria-controls={`${id}-menu`}
        aria-current={current ? 'page' : undefined}
        onClick={() => setOpen(!open)}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="8" r="3.5" />
          <path d="M5 20c.8-3.6 3.6-5.5 7-5.5s6.2 1.9 7 5.5" />
        </svg>
      </button>
      <div id={`${id}-menu`} className="bp-me-pop" hidden={!open}>
        <a
          href="#/signin"
          aria-current={current ? 'page' : undefined}
          onClick={(e) => {
            e.preventDefault();
            setOpen(false);
            onSignIn();
          }}
        >
          Your sign-in
        </a>
        <fieldset className="bp-me-theme">
          <legend>How the app looks</legend>
          {THEME_CHOICES.map(([t, label]) => (
            <label key={t}>
              <input
                type="radio"
                name={`${id}-theme`}
                value={t}
                checked={theme === t}
                onChange={() => pick(t)}
              />
              {label}
            </label>
          ))}
          {!kept && <p className="bp-me-note">This browser won’t keep it: it lasts until you leave.</p>}
        </fieldset>
      </div>
    </div>
  );
}
